import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  getRouteApi,
  Link,
  useLocation,
  useRouter,
} from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { ArrowLeft, Lock } from 'lucide-react';
import { toast } from 'sonner';

import {
  getClusterCategoriesQueryKey,
  getClusterQueryKey,
  getClustersQueryKey,
  type ReviewClusterBodyDecisionsItem,
  useCluster,
  useClusters,
  useNextCluster,
  useReviewCluster,
} from '@/api/generated';
import { ClusterCompareDialog } from '@/components/blocks/clusters/cluster-compare-dialog';
import {
  type ClusterMark,
  ClusterPhotoTile,
  MainPackshotCard,
} from '@/components/blocks/clusters/cluster-photo';
import { ClusterProgress } from '@/components/blocks/clusters/cluster-progress';
import PageLayout from '@/components/blocks/layout/page-layout';
import { ShelvesSkeleton } from '@/components/blocks/shelves/shelves-skeleton';
import { Button } from '@/components/ui/button';
import { CommonError } from '@/components/ui/common-error';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useClusterClaim } from '@/hooks/use-cluster-claim';

const route = getRouteApi('/app/clusters/$clusterId');

type Decision = ReviewClusterBodyDecisionsItem['to'];
const STATUS_MARK: Partial<Record<string, ClusterMark>> = {
  junk: 'junk',
  not_found_sku: 'wrongSku',
};
const MARK_STATUS: Record<ClusterMark, Decision> = {
  junk: 'junk',
  wrongSku: 'not_found_sku',
};

const ClusterReview = ({ clusterId }: { clusterId: string }) => {
  const { t, i18n } = useTranslation();
  const navigate = route.useNavigate();
  const router = useRouter();
  const search = route.useSearch();
  const fromCluster = useLocation({
    select: (location) => location.state.fromCluster === true,
  });
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useCluster(Number(clusterId));
  const review = useReviewCluster();
  const nextCluster = useNextCluster();
  // Locked to this reviewer while the page is open; read-only when someone
  // else has it.
  const { claim, setClaim } = useClusterClaim(clusterId);
  const locked = claim?.mine === false;
  // The progress of the list the reviewer came from.
  const queue = useClusters({
    category: search.category?.length ? search.category : undefined,
  });
  // The reviewer's choices on this page; null = approved. A photo without an
  // entry keeps its saved decision (approved when it is still unreviewed).
  const [marks, setMarks] = useState<Record<string, ClusterMark | null>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  type Photo = NonNullable<typeof data>['photos'][number];
  const main = data?.photos.find((photo) => photo.primary) ?? data?.photos[0];
  const others = data?.photos.filter((photo) => photo !== main) ?? [];
  // The server sends photos still to review first, then the decided ones.
  const waiting = others.filter((photo) => photo.status === 'unreviewed');
  const decided = others.filter((photo) => photo.status !== 'unreviewed');
  const open = others.find((photo) => photo.packshotId === openId) ?? null;

  const markOf = (photo: Photo) =>
    photo.packshotId in marks
      ? (marks[photo.packshotId] ?? undefined)
      : STATUS_MARK[photo.status];
  const decisionOf = (photo: Photo): Decision => {
    const mark = markOf(photo);
    return mark ? MARK_STATUS[mark] : 'reviewed';
  };
  const isChanged = (photo: Photo) =>
    photo.status !== 'unreviewed' && decisionOf(photo) !== photo.status;

  // Every photo still to review (the main packshot too), plus the decided
  // ones the reviewer changed; untouched decisions keep their reviewer.
  const fresh = main?.status === 'unreviewed' ? [main, ...waiting] : waiting;
  const changed = decided.filter(isChanged);
  const decisions: Array<ReviewClusterBodyDecisionsItem> = [
    ...fresh.map((photo) => ({
      packshotId: photo.packshotId,
      from: 'unreviewed' as const,
      to: decisionOf(photo),
    })),
    ...changed.map((photo) => ({
      packshotId: photo.packshotId,
      from: photo.status as ReviewClusterBodyDecisionsItem['from'],
      to: decisionOf(photo),
    })),
  ];
  let doneLabel = t('clusters.review.done');
  if (locked) doneLabel = t('clusters.review.nextFree');
  else if (decisions.length === 0) doneLabel = t('clusters.review.nextCluster');
  const freshCount = (decision: Decision) =>
    fresh.filter((photo) => decisionOf(photo) === decision).length;
  // Marks Previous would throw away; approving is the default, so not those.
  const unsaved = fresh.some((photo) => markOf(photo)) || changed.length > 0;

  const toggle = (photo: Photo, kind: ClusterMark) => {
    const next = markOf(photo) === kind ? null : kind;
    setMarks((prev) => ({ ...prev, [photo.packshotId]: next }));
  };

  // The server picks and locks the next cluster nobody else has open.
  const goNext = async () => {
    let nextId: string | null = null;
    try {
      ({ id: nextId } = await nextCluster.mutateAsync({
        data: {
          category: search.category?.length ? search.category : undefined,
          excludeId: clusterId,
        },
      }));
    } catch {
      // Back to the list, which shows what is still free.
    }
    await (nextId
      ? navigate({
          params: { clusterId: nextId },
          search,
          state: { fromCluster: true },
        })
      : navigate({ to: '/app/clusters', search }));
  };

  // The browser's own back step, to the cluster Done came from.
  const goPrevious = () => {
    setConfirmLeave(false);
    router.history.back();
  };

  const finish = async () => {
    // Stays set until the page moves on (it remounts per cluster), so a second
    // Enter while the next cluster loads can't re-send this one.
    if (saving) return;
    setSaving(true);
    // Nothing to save (a finished cluster nobody changed), or nothing this
    // reviewer may save: go straight on.
    if (locked || decisions.length === 0) {
      await goNext();
      return;
    }
    let skipped: number;
    try {
      ({ skipped } = await review.mutateAsync({
        clusterId: Number(clusterId),
        data: { decisions },
      }));
    } catch (error) {
      const holder: unknown = isAxiosError(error)
        ? (error.response?.data as { holder?: unknown } | undefined)?.holder
        : undefined;
      if (isAxiosError(error) && error.response?.status === 409) {
        // Someone else took the cluster while this page was open.
        setClaim({
          mine: false,
          holder: typeof holder === 'string' ? holder : null,
          since: null,
        });
        toast.error(t('clusters.review.lockLost', { holder: holder ?? '' }), {
          position: 'top-center',
        });
      } else {
        toast.error(t('clusters.review.saveFailed'), {
          position: 'top-center',
        });
      }
      setSaving(false);
      return;
    }
    const count = (decision: Decision) =>
      decisions.filter((item) => item.to === decision).length;
    if (skipped === decisions.length) {
      toast.warning(t('clusters.review.alreadySaved', { id: clusterId }), {
        position: 'top-center',
      });
    } else if (skipped > 0) {
      toast.warning(t('clusters.review.partlySkipped', { count: skipped }), {
        position: 'top-center',
      });
    } else {
      toast.success(
        t('clusters.review.saved', {
          id: clusterId,
          approved: count('reviewed'),
          junk: count('junk'),
          wrongSku: count('not_found_sku'),
        }),
        { position: 'top-center' }
      );
    }
    await queryClient.invalidateQueries({
      queryKey: getClustersQueryKey(),
      refetchType: 'none',
    });
    await queryClient.invalidateQueries({
      queryKey: getClusterCategoriesQueryKey(),
    });
    // Previous must load this cluster fresh, not the cached pre-save photos.
    queryClient.removeQueries({
      queryKey: getClusterQueryKey(Number(clusterId)),
    });
    await goNext();
  };

  // The cluster just became this reviewer's (a lock expired): its photos may
  // have changed while someone else had it, so load them again.
  const wasLocked = useRef(false);
  useEffect(() => {
    if (wasLocked.current && claim?.mine) {
      setMarks({});
      queryClient
        .invalidateQueries({ queryKey: getClusterQueryKey(Number(clusterId)) })
        .catch(() => undefined);
    }
    wasLocked.current = locked;
  }, [claim, locked, clusterId, queryClient]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || openId || saving || confirmLeave || !claim)
        return;
      if (!(event.target instanceof HTMLElement)) return;
      if (event.target.closest('a, input, textarea, [role="dialog"]')) return;
      // Enter is Done even on a focused tile button, which keeps the focus after
      // a click; preventDefault stops it re-clicking (and undoing) the mark.
      // Space still presses the focused button.
      event.preventDefault();
      finish().catch(() => undefined);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  });

  const grid = (photos: Array<Photo>, isDecided = false) => (
    <div className='grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-4'>
      {photos.map((photo) => (
        <ClusterPhotoTile
          key={photo.packshotId}
          photo={photo}
          mark={markOf(photo)}
          decided={isDecided}
          changed={isChanged(photo)}
          readOnly={locked}
          onOpen={() => {
            setOpenId(photo.packshotId);
          }}
          onMark={(kind) => {
            toggle(photo, kind);
          }}
        />
      ))}
    </div>
  );

  if (isLoading) {
    return <ShelvesSkeleton />;
  }

  if (isError || !data || !main) {
    return <CommonError />;
  }

  // overflow-visible so the main packshot card can stick while scrolling.
  return (
    <PageLayout className='overflow-visible pb-24'>
      <div className='page-top-container'>
        <Button
          data-testid='cluster-review-back'
          variant='outline'
          size='sm'
          className='bg-white'
          asChild>
          <Link
            to='/app/clusters'
            search={search}>
            <ArrowLeft />
            {t('clusters.review.back')}
          </Link>
        </Button>
        <span className='page-top-title'>{t('clusters.review.title')}</span>
      </div>

      <div className='flex flex-col gap-1 px-4'>
        <h1 className='text-xl font-semibold'>
          <span className='text-muted-foreground mr-2 font-mono text-sm'>
            #{data.id}
          </span>
          {data.name}
        </h1>
        <p className='text-muted-foreground text-sm'>
          <Trans
            i18nKey='clusters.review.hint'
            components={{ bold: <b /> }}
          />
        </p>
      </div>

      {claim && !claim.mine && (
        <p
          data-testid='cluster-review-locked'
          className='border-brand/40 bg-brand/10 mx-4 flex items-center gap-2 rounded-xl border p-4 text-sm'>
          <Lock className='size-4 shrink-0' />
          {t('clusters.review.lockedBy', {
            holder: claim.holder ?? t('clusters.review.someoneElse'),
            since: claim.since
              ? new Date(claim.since).toLocaleTimeString(i18n.language, {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '–',
          })}
        </p>
      )}

      {queue.data && (
        <ClusterProgress
          className='px-4'
          progress={queue.data.progress}
          categories={search.category ?? []}
        />
      )}

      <div className='grid items-start gap-6 px-4 md:grid-cols-[18rem_minmax(0,1fr)]'>
        <MainPackshotCard
          photo={main}
          name={data.name}
          others={waiting.length}
        />
        <div className='flex min-w-0 flex-col gap-6'>
          {waiting.length === 0 ? (
            <p className='border-success/40 bg-success/10 rounded-xl border p-4 text-sm'>
              {t('clusters.review.allReviewed')}
            </p>
          ) : (
            grid(waiting)
          )}
          {decided.length > 0 && (
            <section
              data-testid='cluster-reviewed-grid'
              className='flex flex-col gap-3'>
              <h2 className='text-muted-foreground text-sm font-semibold'>
                {t('clusters.review.alreadyReviewed', {
                  count: decided.length,
                })}
              </h2>
              {grid(decided, true)}
            </section>
          )}
        </div>
      </div>

      <ClusterCompareDialog
        original={main}
        selected={open}
        mark={open ? markOf(open) : undefined}
        readOnly={locked}
        onClose={() => {
          setOpenId(null);
        }}
        onMark={(kind) => {
          // Every button in the dialog closes it, back to the grid.
          if (open) setMarks((prev) => ({ ...prev, [open.packshotId]: kind }));
          setOpenId(null);
        }}
        onApprove={() => {
          if (open) setMarks((prev) => ({ ...prev, [open.packshotId]: null }));
          setOpenId(null);
        }}
      />

      <Dialog
        open={confirmLeave}
        onOpenChange={setConfirmLeave}>
        <DialogContent
          data-testid='cluster-review-leave-dialog'
          className='max-w-md'>
          <DialogTitle>{t('clusters.review.leaveTitle')}</DialogTitle>
          <DialogDescription>
            {t('clusters.review.leaveText')}
          </DialogDescription>
          <div className='flex justify-end gap-2'>
            <Button
              data-testid='cluster-review-leave-stay'
              variant='outline'
              onClick={() => {
                setConfirmLeave(false);
              }}>
              {t('clusters.review.stay')}
            </Button>
            <Button
              data-testid='cluster-review-leave-confirm'
              variant='destructive'
              onClick={goPrevious}>
              {t('clusters.review.leave')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className='bg-background fixed inset-x-0 bottom-0 z-40 border-t shadow-[0_-6px_18px_rgb(0_0_0/0.05)]'>
        <div className='flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3'>
          <span className='font-semibold'>
            {t('clusters.review.approved', { count: freshCount('reviewed') })}
          </span>
          <span className='text-muted-foreground text-sm'>
            {t('clusters.review.marked', {
              junk: freshCount('junk'),
              wrongSku: freshCount('not_found_sku'),
            })}
            {changed.length > 0 &&
              ` · ${t('clusters.review.changedCount', { count: changed.length })}`}
            {decided.length > 0 &&
              ` · ${t('clusters.review.earlierCount', { count: decided.length })}`}
          </span>
          <span className='flex-1' />
          {fromCluster && (
            <Button
              data-testid='cluster-review-previous'
              variant='outline'
              size='lg'
              disabled={saving}
              onClick={() => {
                if (unsaved) setConfirmLeave(true);
                else goPrevious();
              }}>
              <ArrowLeft />
              {t('clusters.review.previous')}
            </Button>
          )}
          <Button
            data-testid='cluster-review-done'
            size='lg'
            className='bg-success text-success-foreground hover:bg-success/90'
            disabled={saving || !claim}
            onClick={() => {
              finish().catch(() => undefined);
            }}>
            {doneLabel}
            <kbd className='rounded border border-current px-1 font-mono text-[10px] opacity-70'>
              ⏎
            </kbd>
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};

// Keyed by cluster so marks never carry over to the next cluster.
const ClusterReviewPage = () => {
  const { clusterId } = route.useParams();
  return (
    <ClusterReview
      key={clusterId}
      clusterId={clusterId}
    />
  );
};

export default ClusterReviewPage;
